// @ts-strict-ignore
import { Component, computed, EventEmitter, Input, model, OnInit, Output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ProjectHistoryTableEntry, ProjectVersionHistoryTableComponent } from 'app/modules/project/components/project/project-version-history/project-version-history-table/project-version-history-table.component';
import { SvgIconComponent } from 'app/modules/shared/svg-icon/svg-icon.component';
import { TooltipDirective } from 'app/modules/shared/tooltip/tooltip.directive';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NumberUtils } from '@shared/util/number-utils';
import { ProjectHeadVO, ProjectHistoryEntryVO, ProjectHistoryVO, ProjectVO } from '@project/model/vo/project.vo';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UserUtils } from '@user/service/user-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectType } from '../../../model/project-type';
import { DateFormatter } from '@shared/date-formatter';
import { ProjectModel } from '@project/model/project.model';
import { ProjectOrderingUtils } from '@project/components/project/util/project-ordering-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { LoadProjectService } from '../../../service/load-project.service';
import { ProjectDataService } from '../../../service/project-data.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { LoadedProjectData } from '../../../service/loaded-project.data';
import { UserModel } from '@user/model/user.model';
import { ArticleNumberLong } from '../../../model/article-number-long';
import { ProjectHistoryService } from '../../../history/project-history.service';
import { lastValueFrom } from 'rxjs';

class ProjectStatus {
  projectId: number;
  projectVersion: number;
  status: string;
  versionLabel: string;
  isProjectLocked: boolean;
}

@Component({
  selector: 'app-project-version-history',
  templateUrl: './project-version-history.component.html',
  imports: [
    TranslateModule,
    TooltipDirective,
    SvgIconComponent,
    TooltipDirective,
    ProjectVersionHistoryTableComponent
  ]
})
export class ProjectVersionHistoryComponent implements OnInit {
  @Input() project: ProjectHeadVO;
  @Input() compareItemListMode: boolean = false;
  @Output() public finish = new EventEmitter<boolean | undefined>();

  public history = signal<ProjectHistoryVO | undefined>(undefined);

  public selectedEntry = model<ProjectHistoryTableEntry | undefined>(undefined);

  public metaInfo = computed(() => {
    const history = this.history();
    if (!history) return undefined;
    return {
      projectTypeLabel: this.getProjectTypeLabel(history.projectType),
      projectOwnerUsername: this.projectHistoryService.getDisplayUsername(history.projectOwner),
      projectStatus: this.buildProjectStatus(history),
      outdatedInfo: this.getOutdatedInfo(history),
    }
  });

  versionLoadedByTemporaryLink: number = null;

  public update = new EventEmitter<void>();

  public isConstruction = false;

  constructor(
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private projectDataService: ProjectDataService,
    private loadProjectService: LoadProjectService,
    private localeResourceManager: LocaleResourceManager,
    private alertHelper: AlertHelper,
    private projectComponentPresenter: ProjectComponentPresenter,
    private projectHistoryService: ProjectHistoryService,
  ) {
  }


  ngOnInit(): void {
    this.setVersionLoadedByTemporaryLink();
    void this.loadProjectHistory();
    this.isConstruction = this.userModel.isConstruction && (!this.userModel.user || this.userModel.user.isProduction());
  }

  private setVersionLoadedByTemporaryLink(): void {
    if (this.project && this.project.temporaryLinkGUID) {
      this.versionLoadedByTemporaryLink = this.project.currentVersion;
    }
  }

  updateViewList(): void {
    this.update.emit();
  }

  public onCancel(projectLoaded?: boolean): void {
    this.bsModalRef.hide();
    this.finish.emit(projectLoaded);
  }

  private async loadProjectHistory() {
    if (this.project && NumberUtils.isNumber(this.project.id)) {
      let projectHistory;
      try {
        projectHistory = await lastValueFrom(this.projectDataService.loadProjectHistory(this.project.id));
      } finally {
        if (projectHistory) {
          this.history.set(projectHistory);
          this.updateViewList();
        } else {
          this.alertHelper.showError(
            'projectVersionHistoryPopup.error.title.load_project_version_history',
            'projectVersionHistoryPopup.error.message.load_project_version_history'
          );
        }
      }
    }
  }

  private getOutdatedInfo(projectHistory: ProjectHistoryVO): string | undefined {
    if (!NumberUtils.isNumber(projectHistory.materialStatus) || !ArticleUtils.isStatusOutdated(projectHistory.materialStatus)) {
      return undefined;
    }
    return ArticleUtils.getOutdatedConfigurationText(this.localeResourceManager, '', projectHistory.materialStatus, projectHistory.lastCall) +
      ' (' +
      projectHistory.materialStatus +
      ')';
  }

  private buildProjectStatus(projectHistory: ProjectHistoryVO): ProjectStatus {
    if (!projectHistory || !projectHistory.projectHistoryEntries) {
      return null;
    }

    let projectStatus: ProjectStatus = null;
    const releasedEntry: ProjectHistoryEntryVO = projectHistory.projectHistoryEntries
      .concat()
      .sort((e1, e2) => this.compareDatesDesc(e1.date, e2.date))
      .find((entry) => ProjectOrderingUtils.isReleasedForProductionState(entry.finalizationState));
    if (releasedEntry) {
      if (!projectHistory.isProjectLocked) {
        projectStatus = new ProjectStatus();
        projectStatus.status = this.localeResourceManager.getValueByKey('projectVersionHistoryPopup.status.released_for_production');
        projectStatus.versionLabel = this.getProjectVersionLabel(releasedEntry);
        if (releasedEntry.projectVersionSaveDate) {
          projectStatus.versionLabel += ` (${DateFormatter.formatDate(releasedEntry.projectVersionSaveDate)})`;
        }
        projectStatus.projectId = releasedEntry.projectId;
        projectStatus.projectVersion = releasedEntry.projectVersion;
      } else {
        projectStatus = new ProjectStatus();
        projectStatus.status = this.localeResourceManager.getValueByKey('load.message.project_is_locked_for_production');
      }
      projectStatus.isProjectLocked = projectHistory.isProjectLocked;
    }

    return projectStatus;
  }

  private compareDatesDesc(date1: Date, date2: Date): number {
    if (!date1 && !date2) {
      return 0;
    }
    if (!date1) {
      return 1;
    }
    if (!date2) {
      return -1;
    }
    return date2.getTime() - date1.getTime();
  }

  private getProjectTypeLabel(projectType: ProjectType): string {
    if (!projectType) {
      return '';
    }

    let languageKey: string = null;
    if (projectType === "CIRCUIT_BOARD" || projectType === "FREE_MOUNTING") {
      languageKey = 'newproject.assistant.free_assembly';
    } else if (projectType === "GDS" || projectType === "MCS") {
      languageKey = 'newproject.assistant.MCS_AND_GDS';
    } else if (projectType === "MARKINGSYSTEM") {
      languageKey = 'newproject.assistant.marking_system';
    } else if (projectType === "SPEEDWAY") {
      languageKey = 'newproject.assistant.speedway';
    } else if (projectType === "XCOM") {
      languageKey = 'newproject.assistant.mounting_rail';
    } else if (projectType === "MOUNTING_RAIL") {
      languageKey = 'newproject.assistant.mounting_rail';
    } else if (projectType === "SPLICING_CONNECTOR_221") {
      languageKey = 'newproject.assistant.splicing_connector_221';
    } else if (projectType === "LTL") {
      languageKey = 'newproject.assistant.ltl';
    }

    if (!languageKey) {
      // If no language key is found, return the project type as a fallback
      return projectType;
    } else {
      return this.localeResourceManager.getValueByKey(languageKey);
    }
  }


  private getProjectVersionLabel(historyEntry: ProjectHistoryEntryVO): string {
    if (!historyEntry) {
      return '';
    }
    const versionLabel: string = historyEntry.projectVersionDisplayString;
    if (historyEntry.documentVersion > -1 && UserUtils.isWagoUser(this.projectModel.user)) {
      return (!versionLabel ? '-' : versionLabel) + ' / ' + historyEntry.documentVersion;
    } else {
      return versionLabel;
    }
  }


  public async loadProject(projectId: number, projectVersion: number) {
    if (await this.loadProjectService.checkConfirmLoad()) {
      const loadedProjectData = await this.loadProjectService.loadProjectById(projectId, projectVersion);
      this.loadedProject(loadedProjectData);
    }
  }


  public compareItemList(historyEntry: ProjectHistoryTableEntry): void {
    this.onCancel(false);
    const project: ProjectVO = new ProjectVO();
    project.id = historyEntry.projectId;
    project.currentVersion = historyEntry.projectVersion;
    project.currentVersionDisplayString = historyEntry.projectVersionLabel;
    project.modificationDate = historyEntry.date;
    project.name = this.history().projectName;
    project.articleNumberLong = new ArticleNumberLong(this.history().articleNumberLong);
    this.projectComponentPresenter.openCompareItemListComponent(project, this.history().projectPath);
  }

  public openHELPLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`helplink.versioning`), `_blank`);
  }


  private loadedProject(data: LoadedProjectData) {
    if (data.success) {
      this.onCancel(true);
    }
  }
}
